import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Disc3, Loader2 } from "lucide-react";

const BG_URL =
  "https://images.unsplash.com/photo-1655931546417-18e16c015812?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxoZWFkcGhvbmVzJTIwZGFyayUyMHN0dWRpb3xlbnwwfHx8fDE3Nzg3Mzg1ODR8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(email, password, name);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#0A0A0A] text-white">
      <div className="lg:col-span-2 flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm space-y-7"
          data-testid="register-form"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              // new account
            </div>
            <h2 className="mt-2 font-display font-black text-4xl tracking-tighter">Create account</h2>
            <p className="text-white/50 mt-2 text-sm">
              Already have one?{" "}
              <Link to="/login" className="text-[#FF3B30] hover:underline" data-testid="link-to-login">
                Sign in
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="DJ Whoever"
                className="mt-2 bg-transparent border-white/10 text-white placeholder:text-white/30 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@studio.com"
                className="mt-2 bg-transparent border-white/10 text-white placeholder:text-white/30 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="min 6 characters"
                className="mt-2 bg-transparent border-white/10 text-white placeholder:text-white/30 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="register-password-input"
              />
            </div>
          </div>

          {error && (
            <div className="border border-[#FF3B30]/60 bg-[#FF3B30]/10 text-white px-3 py-2 text-sm rounded-sm font-mono" data-testid="register-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#FF3B30] hover:bg-[#FF5C53] text-white rounded-sm font-display font-bold tracking-wide uppercase text-sm transition-colors"
            data-testid="register-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
          </Button>
        </form>
      </div>

      <div
        className="hidden lg:block lg:col-span-3 relative order-1 lg:order-2"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.9) 100%), url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="register-hero"
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-10 w-10 items-center justify-center bg-[#FF3B30] rounded-sm">
              <Disc3 className="h-6 w-6 text-white" strokeWidth={2.2} />
            </span>
            <div className="font-display font-black text-2xl tracking-tighter">LYRIQ</div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
              // 02 — onboarding
            </div>
            <h1 className="font-display font-black tracking-tighter text-5xl xl:text-7xl leading-[0.9] max-w-lg">
              Build the library only <span className="text-[#FF3B30]">you</span> can sing.
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
}
